import { infiniteQueryOptions, type InfiniteData } from '@tanstack/react-query';

import { emptyListData } from '~/constants';
import {
  chatApi,
  QK_FETCH_CONVERSATIONS,
  QK_FETCH_CONVERSATION_MESSAGES,
} from '~/requests/chatApi';

import type { IConversationVM, IMessageVM, TListResponse } from '~/types';

export function conversationsOptions(): Omit<
  ReturnType<
    typeof infiniteQueryOptions<
      TListResponse<IConversationVM>,
      Error,
      InfiniteData<TListResponse<IConversationVM>, unknown>,
      Array<string>,
      number
    >
  >,
  'queryFn'
> {
  return infiniteQueryOptions({
    initialPageParam: 1,
    queryKey: [QK_FETCH_CONVERSATIONS],
    queryFn: async ({ pageParam, signal }) => {
      return chatApi
        .getConversations({
          signal,
          page: pageParam,
        })
        .catch(() => {
          return emptyListData as TListResponse<IConversationVM>;
        });
    },
    getNextPageParam: ([, meta]) => {
      return meta.nextPage;
    },
    getPreviousPageParam: ([, meta]) => {
      return meta.prevPage ?? 1;
    },
  });
}

interface IChatOptionsParams {
  conversationId: string;
}

type TChatOptions = Omit<
  ReturnType<
    typeof infiniteQueryOptions<
      TListResponse<IMessageVM>,
      Error,
      InfiniteData<TListResponse<IMessageVM>, unknown>,
      Array<string | Required<IChatOptionsParams>>,
      number
    >
  >,
  'queryFn'
>;

export function conversationMessagesOptions({
  conversationId = '',
}: IChatOptionsParams): TChatOptions {
  return infiniteQueryOptions({
    initialPageParam: 1,
    queryKey: [QK_FETCH_CONVERSATION_MESSAGES, { conversationId }],
    queryFn: async ({ pageParam, signal }) => {
      return chatApi
        .getConversationMessages({
          signal,
          conversationId,
          page: pageParam,
        })
        .catch(() => {
          return emptyListData as TListResponse<IMessageVM>;
        });
    },
    getNextPageParam: (data) => {
      if (!data) return null;
      const [, meta] = data;
      return meta.nextPage;
    },
    getPreviousPageParam: (data) => {
      if (!data) return null;
      const [, meta] = data;
      return meta.prevPage ?? 1;
    },
  });
}
